import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";

export function AccountDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [sec, setSec] = useState(null);
  const [savingSec, setSavingSec] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent(""); setNext(""); setConfirm("");
      api.get("/auth/security").then((r) => setSec(r.data)).catch(() => {});
    }
  }, [open]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const saveSecurity = async () => {
    setSavingSec(true);
    try {
      const { data } = await api.put("/auth/security", { mfa_enabled: sec.mfa_enabled, mfa_phone: sec.mfa_phone });
      setSec((p) => ({ ...p, ...data }));
      toast.success("Security settings saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingSec(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Account</DialogTitle>
          <DialogDescription>Manage your password and two-factor security.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="password">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="password" data-testid="tab-password"><KeyRound className="h-4 w-4 mr-1.5" /> Password</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck className="h-4 w-4 mr-1.5" /> Security</TabsTrigger>
          </TabsList>

          <TabsContent value="password">
            <form onSubmit={changePassword} className="space-y-4 pt-2" data-testid="change-password-form">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Current password</Label>
                <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" className="mt-1.5" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New password</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" className="mt-1.5" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm new password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" className="mt-1.5" required />
              </div>
              <Button type="submit" disabled={savingPw} data-testid="save-password-button" className="w-full">
                {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="security">
            {sec && (
              <div className="space-y-4 pt-2" data-testid="security-form">
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Two-factor by SMS</p>
                    <p className="text-xs text-muted-foreground">Require a texted code at sign-in.</p>
                  </div>
                  <Switch checked={sec.mfa_enabled} onCheckedChange={(v) => setSec({ ...sec, mfa_enabled: v })} data-testid="mfa-enabled-switch" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile for codes (04… or +61…)</Label>
                  <Input value={sec.mfa_phone} onChange={(e) => setSec({ ...sec, mfa_phone: e.target.value })} data-testid="mfa-phone-input" className="mt-1.5" placeholder="0457 571 430" />
                </div>
                {!sec.twilio_enabled && (
                  <p className="text-xs text-muted-foreground rounded-md bg-muted/60 border border-border px-3 py-2" data-testid="mfa-sim-note">
                    Twilio is in simulation mode, so codes aren't actually texted yet. Two-factor turns on automatically once live Twilio credentials are added.
                  </p>
                )}
                <Button onClick={saveSecurity} disabled={savingSec} variant="secondary" data-testid="save-security-button" className="w-full">
                  {savingSec ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save security settings"}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
